/**
 * Reusable button component with loading and disabled states
 * Epic 8 Story 8.1.3 - Design System Button Component
 * Follows design system: docs/ui-ux-technical-specification.md
 *
 * @module components/ui/Button
 */

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';
import { type LucideIcon, Loader2 } from 'lucide-react';
import { useTranslation } from '@/locales';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant
   * Story 8.7: Added 'golden' variant for magical CTAs with lamplight glow
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'golden'
    | 'twilight-outline';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Loading state - shows spinner and disables button */
  loading?: boolean;
  /** Optional icon (Lucide icon component) */
  icon?: LucideIcon;
  /** Full width button */
  fullWidth?: boolean;
  /** Optional loading text - defaults to translation 'common.button.loading' */
  loadingText?: string;
}

/**
 * Button component with consistent styling across the app
 * Implements WCAG AA accessibility (FR92-FR96)
 * Touch targets ≥44x44px (grandparent-proof design)
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon: Icon,
      fullWidth = false,
      disabled,
      className,
      loadingText,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Base classes with animation utilities from Story 8.1.2
    // Story 11.27: Changed rounded-sm to rounded-lg for more premium feel
    const baseClasses =
      'inline-flex items-center justify-center font-medium transition-fast ease-in-out rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-11 min-h-11';

    // Variant classes with hover translateY effect
    // Story 8.7: Added 'golden' variant for magical CTAs
    const variantClasses = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 hover:-translate-y-0.5 focus:ring-primary-500 active:bg-primary-700 shadow-md',
      secondary:
        'bg-secondary-500 text-white hover:bg-secondary-600 hover:-translate-y-0.5 focus:ring-secondary-500 active:bg-secondary-700 shadow-md',
      outline:
        'border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:-translate-y-0.5 focus:ring-gray-500 active:bg-gray-100',
      ghost:
        'text-gray-700 hover:bg-gray-100 hover:-translate-y-0.5 focus:ring-gray-500 active:bg-gray-200',
      danger:
        'bg-error-500 text-white hover:bg-error-600 hover:-translate-y-0.5 focus:ring-error-500 active:bg-error-700 shadow-md',
      // Story 8.7: Golden variant with lamplight glow effect
      // Use for primary CTAs in twilight theme - inviting, magical feel
      // Story 11.27: Pill-shaped (rounded-full) for premium feel per PO feedback
      golden:
        'bg-accent-gold text-twilight-deep hover:bg-accent-gold-light hover:-translate-y-0.5 focus:ring-accent-gold active:bg-secondary-600 shadow-md glow-gold hover:glow-gold-hover rounded-full',
      // Story 12.7: Twilight outline variant for dark backgrounds
      'twilight-outline':
        'border-2 border-white/20 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/30 hover:-translate-y-0.5 focus:ring-accent-gold/30 active:bg-white/20',
    };

    // Size classes ensuring ≥44x44px touch targets
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm gap-1.5', // 36x36px base, min-w-11 min-h-11 ensures 44x44px
      md: 'px-4 py-2 text-base gap-2', // 40x40px base, min-w-11 min-h-11 ensures 44x44px
      lg: 'px-6 py-3 text-lg gap-2', // 48x48px+ naturally meets requirements
    };

    const widthClass = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          widthClass,
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            <span>{loadingText ?? t('common.button.loading')}</span>
          </>
        ) : (
          <>
            {Icon && <Icon className="w-5 h-5" aria-hidden="true" />}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
